// Recipe photography manifest.
//
// An image is only shown when `verified` is true: a vision check (scripts/images/pipeline.ts)
// confirmed it depicts this recipe's dish and visible ingredients, with nothing contradicting
// them. Unverified or missing images fall back to a clearly abstract placeholder, never to an
// unrelated photo. Tests enforce: files exist, recipes share an image only when their looks are
// at least 90% similar (imageCache.ts), and verified entries carry their check details.

import manifest from './recipeImages.json';
import { generatedRecipe } from './generated';

export interface RecipeImageEntry {
  /** Public path of the large version, e.g. /recipes/<cache-key>-1080.webp */
  src: string;
  /** Smaller version for cards and thumbnails. */
  src640: string | null;
  /** Image cache key (hash of the dish's look) this recipe uses. */
  cacheKey: string | null;
  /** 1 when generated for this recipe's exact look; below 1 when reused from a similar dish. */
  similarity: number | null;
  width: number;
  height: number;
  alt: string;
  /** Generated from the recipe's structured visual description, or a licensed photo. */
  origin: 'generated' | 'photo' | 'cached';
  model: string | null;
  promptVersion: string | null;
  verified: boolean;
  checkedAt: string | null;
  checkNote: string | null;
}

const IMAGES = manifest as Record<string, RecipeImageEntry>;

export function recipeImage(recipeId: string): RecipeImageEntry | null {
  const written = generatedRecipe(recipeId);
  if (written) {
    // Amari's own recipes: a photo that passed the same kind of vision check, served from Supabase Storage.
    return written.imageUrl ? {
      src: written.imageUrl, src640: null, cacheKey: null, similarity: 1, width: 896, height: 1120,
      alt: `Photo of ${written.recipe.title}`, origin: 'generated', model: null, promptVersion: null, verified: true, checkedAt: null, checkNote: null,
    } : null;
  }
  const img = IMAGES[recipeId];
  return img && img.verified ? img : null;
}

export function allImageEntries(): [string, RecipeImageEntry][] {
  return Object.entries(IMAGES);
}
